import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiArrowRight, FiEye, FiEyeOff } from "react-icons/fi";
import { FaSteam } from "react-icons/fa";
import AuthShell, { AuthFooter, PasswordBox } from "../MVP/AuthShell";
import {
  FormField,
  Field,
  Action,
  Divider,
  request,
  acceptSession,
  steamLogin,
} from "../MVP/shared";
import { useLanguage } from "../../contexts/LanguageContext";
import { localizeAuthError } from "../../lib/apiErrors";
import { ApiError } from "../MVP/shared";
export default function Login() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [show, setShow] = useState(false),
    [busy, setBusy] = useState(false),
    [errors, setErrors] = useState<Record<string, string>>({});
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = t("auth.err.email");
    if (!password) next.password = t("auth.err.password");
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const data = await request("/auth/login", { email: email.trim(), password });
      acceptSession(data);
      nav("/");
    } catch (e) {
      const msg = localizeAuthError((e as Error).message, t, e instanceof ApiError ? e.status : undefined);
      toast.error(msg, {
        id: "login-error",
        style: { background: "#7f1d1d", color: "#fff", border: "1px solid #ef4444", borderRadius: "12px", fontSize: "13px" },
        iconTheme: { primary: "#fff", secondary: "#7f1d1d" },
      });
    } finally {
      setBusy(false);
    }
  }
  const err = (key: string) =>
    errors[key] ? (
      <small id={`login-err-${key}`} role="alert" style={{ color: "#ff9191" }}>
        {errors[key]}
      </small>
    ) : null;
  return (
    <AuthShell title={t("auth.login.title")} description={t("auth.login.lead")}>
      <form onSubmit={submit} noValidate>
        <FormField>
          Email
          <Field
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "login-err-email" : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
          {err("email")}
        </FormField>
        <FormField>
          {t("auth.password")}
          <PasswordBox>
            <Field
              type={show ? "text" : "password"}
              autoComplete="current-password"
              placeholder={t("auth.password_enter")}
              value={password}
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? "login-err-password" : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              aria-label={show ? t("auth.hide_password") : t("auth.show_password")}
              aria-pressed={show}
              onClick={() => setShow(!show)}
            >
              {show ? <FiEyeOff /> : <FiEye />}
            </button>
          </PasswordBox>
          {err("password")}
        </FormField>
        <Link style={{ justifySelf: "end" }} to="/forgot-password">
          {t("auth.forgot")}
        </Link>
        <Action $variant="primary" type="submit" disabled={busy} aria-busy={busy}>
          {busy ? t("auth.signing_in") : t("auth.sign_in")}
          <FiArrowRight aria-hidden="true" />
        </Action>
      </form>
      <Divider>{t("auth.or")}</Divider>
      <Action
        type="button"
        disabled={busy}
        onClick={() => steamLogin().catch((e) => toast.error(e.message))}
      >
        <FaSteam />
        {t("auth.login.steam")}
      </Action>
      <AuthFooter>
        {t("auth.no_account")}
        <Link to="/register">{t("auth.register.title")}</Link>
      </AuthFooter>
    </AuthShell>
  );
}
